import { html, useState } from '/vendor/htm-preact.module.js';

/**
 * Choosing a picture for something. GitHub issue #66.
 *
 * The body of the POST is the FILE and nothing else — the two short strings
 * that say what it is for travel in the query string. No multipart, so no
 * multipart parser to feed attacker-chosen bytes to, and no second dependency
 * on the server for the privilege.
 *
 * `fetch` directly rather than `/api.js`'s `post`, which sends JSON. Base64 in
 * a JSON body would be a third larger on the wire, off a phone, for nothing.
 *
 * SHRUNK BEFORE IT IS SENT. A phone camera produces 3-5 MB and the server
 * keeps a picture 1024px on its longest edge, so sending the original would
 * be uploading forty times what survives — off a phone, on pub wifi, into a
 * request body that is buffered in full before any handler runs. The box has
 * 3.9 GB, no swap and no memory limit on the container; `cueforge/bodylimit.py`
 * exists because of what that already cost once.
 *
 * This is an OPTIMISATION, NOT A TRUST BOUNDARY. The server decodes, strips
 * and re-encodes whatever arrives regardless — see `cueforge/images.py`. A
 * caller who skips this and posts something huge is refused by size; one who
 * posts something malicious is refused by `prepare`. Nothing here is relied
 * upon for safety, which is why it is allowed to be best-effort: if the
 * canvas path fails for any reason, the original is sent and the server
 * decides.
 *
 * It also happens to solve HEIC. An iPhone hands over a format Pillow cannot
 * read without a plugin, and the browser that took the photo can decode it
 * natively — so drawing it to a canvas and exporting JPEG is what makes an
 * iPhone photo work at all.
 *
 * NOTHING SHOWS UNTIL AN ADMIN APPROVES IT, and this says so plainly rather
 * than appearing to have worked. A picture that silently does not appear is
 * indistinguishable from a broken upload, and the person who chose it is the
 * one who has to be told which it was.
 */
// The longest edge the server keeps. Sending more than this is sending
// pixels that are about to be thrown away.
const MAX_EDGE = 1024;
const QUALITY = 0.85;

async function shrink(file) {
  // Best-effort by design: any failure here falls back to the original and
  // lets the server decide. See the module docstring.
  try {
    if (!('createImageBitmap' in window)) return file;
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width,
                                                  bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close && bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    // If the round trip made it BIGGER -- a small PNG logo re-encoded as
    // JPEG can -- keep whichever is smaller. Either is fine to the server.
    if (!blob) return file;
    return blob.size < file.size ? blob : file;
  } catch (e) {
    return file;
  }
}

export function ImageUpload({ kind, subjectId, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState('');

  const choose = async (event) => {
    const file = event.target.files && event.target.files[0];
    // The input is cleared either way, so choosing the same file again after
    // a failure still fires a change.
    event.target.value = '';
    if (!file) return;
    setBusy(true); setFailed(''); setSent(false);
    try {
      const url = `/api/images?kind=${encodeURIComponent(kind)}`
                + `&subject_id=${encodeURIComponent(subjectId)}`;
      const body = await shrink(file);
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/octet-stream' },
        body,
      });
      if (!r.ok) {
        const said = await r.json().catch(() => ({}));
        throw new Error(said.detail || `that did not work (${r.status})`);
      }
      setSent(true);
      // NOTHING TO REFRESH, and asking for one is actively harmful. What was
      // just uploaded is PENDING, which is invisible to everybody including
      // the person who chose it -- so there is no new state for the parent
      // to fetch. And on a screen that blanks itself while reloading (the
      // circle screen sets `rows` to null and early-returns "Loading..."),
      // the refresh unmounts this component and takes `sent` with it, so the
      // confirmation vanishes before it can be read. `onDone` is kept for a
      // caller that genuinely has something to do.
      if (onDone) await onDone();
    } catch (e) {
      setFailed(e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="stack" data-testid=${`image-upload-${kind}`}>
      <label>${label}</label>
      <input type="file" accept="image/*" disabled=${busy}
             data-testid=${`image-file-${kind}`} onChange=${choose} />
      ${sent && html`
        <p class="sub" data-testid="image-waiting" style="text-transform:none">
          Sent. It will appear once somebody has approved it.
        </p>`}
      ${failed && html`
        <p class="error" data-testid="image-error">${failed}</p>`}
    </div>`;
}

/**
 * A picture, or nothing at all.
 *
 * Nothing, deliberately: Nick chose that a venue with no approved photo looks
 * exactly as it does today. A placeholder frame would show a gap on all
 * twenty-six pubs to advertise a feature one of them uses.
 */
export function Picture({ imageId, alt, className, style }) {
  if (!imageId) return null;
  return html`
    <img class=${className} style=${style} data-testid="picture"
         alt=${alt || ''} loading="lazy"
         src=${`/api/images/${encodeURIComponent(imageId)}`} />`;
}
